import { PROJECTION_UNAVAILABLE_LABELS, serviceLabel } from 'lib';
import { useTranslation } from 'react-i18next';
import { badge, fmtCount, fmtDate, fmtPercent, fmtSince } from './helpers';

const card = 'bg-white border border-[#E5E7EB] rounded-xl p-4';
const statLabel = 'text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide';
const statValue = 'text-[20px] font-bold text-[#1E293B] leading-tight';

const Stat = ({ label, value, tone }) => (
  <div className="flex flex-col gap-[2px] min-w-[110px]">
    <span className={statLabel}>{label}</span>
    <span className={`${statValue} ${tone || ''}`}>{value}</span>
  </div>
);

const ServiceCard = ({ service }) => {
  const { t } = useTranslation();
  const pct =
    service.allowance && service.allowance > 0
      ? Math.min(100, Math.round(((service.allowanceConsumed || 0) / service.allowance) * 100))
      : null;

  const barTone =
    pct == null
      ? 'bg-[#E2E8F0]'
      : pct >= 90
        ? 'bg-[#E11D48]'
        : pct >= 80
          ? 'bg-[#EA580C]'
          : 'bg-[#16A34A]';

  return (
    <div className={card}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[14px] font-semibold text-[#1E293B]">
          {serviceLabel(service.service)}
        </span>
        {!service.observed && (
          <span className={badge('muted')}>
            {t('admin.activityLog.health.notObserved', { defaultValue: 'not observed' })}
          </span>
        )}
        {service.pastAlertThreshold && (
          <span className={badge('warn')}>
            {t('admin.activityLog.health.pastThreshold', { defaultValue: 'past alert threshold' })}
          </span>
        )}
        {service.seedFailedAt && (
          <span className={badge('bad')}>
            {t('admin.activityLog.health.seedFailed', { defaultValue: 'budget seed failed' })}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-6 mb-3">
        <Stat
          label={t('admin.activityLog.health.today', { defaultValue: 'Calls today' })}
          value={fmtCount(service.todayCalls)}
        />
        <Stat
          label={t('admin.activityLog.health.failures', { defaultValue: 'Failures' })}
          value={fmtCount(service.todayFailures)}
          tone={service.todayFailures > 0 ? 'text-[#E11D48]' : ''}
        />
        <Stat
          label={t('admin.activityLog.health.failureRate', { defaultValue: 'Failure rate' })}
          value={fmtPercent(service.recentFailureRate)}
        />
        <Stat
          label={t('admin.activityLog.health.remaining', { defaultValue: 'Remaining' })}
          value={service.allowance == null ? '—' : fmtCount(service.allowanceRemaining)}
        />
      </div>

      {pct != null && (
        <>
          <div className="h-[6px] w-full rounded-full bg-[#F1F5F9] overflow-hidden">
            <div className={`h-full ${barTone}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[12px] text-[#64748B]">
              {fmtCount(service.allowanceConsumed)} / {fmtCount(service.allowance)} ({pct}%)
            </span>
            {/*
              Said out loud, every time. Other environments authenticate with the same key and have
              their own databases, so this figure cannot see their spend. An admin who reads it as
              authoritative will think there is budget left that is already gone.
            */}
            {service.allowanceRemainingIsUpperBound && (
              <span className={badge('warn')}>
                {t('admin.activityLog.health.upperBound', {
                  defaultValue: 'upper bound — allowance shared with other environments'
                })}
              </span>
            )}
          </div>
        </>
      )}

      <div className="mt-2 text-[12px] text-[#64748B]">
        {service.projectedExhaustionAt ? (
          <>
            {t('admin.activityLog.health.projected', { defaultValue: 'Projected to run out' })}:{' '}
            <span className="font-semibold text-[#EA580C]">
              {fmtDate(service.projectedExhaustionAt)}
            </span>
          </>
        ) : (
          <>
            {t('admin.activityLog.health.noProjection', { defaultValue: 'No projection' })}
            {service.projectionUnavailableReason
              ? ` — ${
                  PROJECTION_UNAVAILABLE_LABELS[service.projectionUnavailableReason] ||
                  service.projectionUnavailableReason
                }`
              : ''}
          </>
        )}
      </div>
    </div>
  );
};

const JobRow = ({ job }) => {
  const { t } = useTranslation();
  return (
    <tr className="border-b border-[#F1F5F9]">
      <td className="px-3 py-2 text-[13px] font-mono text-[#353E4A]">{job.job}</td>
      <td className="px-3 py-2 text-[12px] text-[#64748B] font-mono">{job.declaredCadence}</td>
      <td className="px-3 py-2 text-[13px] text-[#353E4A] whitespace-nowrap">
        {fmtDate(job.lastRunAt)}
      </td>
      <td className="px-3 py-2 text-[13px] whitespace-nowrap">
        {fmtSince(job.timeSinceLastSuccessMs)}
      </td>
      <td className="px-3 py-2">
        {job.overdue ? (
          <span className={badge('bad')}>
            {job.lastSuccessAt
              ? t('admin.activityLog.health.overdue', { defaultValue: 'overdue' })
              : t('admin.activityLog.health.neverRun', { defaultValue: 'never succeeded' })}
          </span>
        ) : (
          <span className={badge('good')}>
            {t('admin.activityLog.health.onSchedule', { defaultValue: 'on schedule' })}
          </span>
        )}
      </td>
    </tr>
  );
};

const HealthPanel = ({ health, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={`${card} text-center text-[13px] text-[#97A3B5]`}>
        {t('admin.activityLog.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }
  if (!health) return null;

  const overdueCount = (health.jobs || []).filter((j) => j.overdue).length;

  return (
    <div className="flex flex-col gap-4 mb-4">
      <div className="grid gap-4 md:grid-cols-2">
        {(health.services || []).map((s) => (
          <ServiceCard key={s.service} service={s} />
        ))}
      </div>

      <div className={`${card} overflow-x-auto`}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-[14px] font-semibold text-[#1E293B]">
            {t('admin.activityLog.health.jobs', { defaultValue: 'Scheduled jobs' })}
          </span>
          <span className={badge(overdueCount ? 'bad' : 'good')}>
            {overdueCount
              ? t('admin.activityLog.health.overdueCount', {
                  defaultValue: '{{count}} overdue',
                  count: overdueCount
                })
              : t('admin.activityLog.health.allOnSchedule', { defaultValue: 'all on schedule' })}
          </span>
        </div>

        <table className="w-full min-w-[720px] border-collapse">
          <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
            <tr>
              {[
                t('admin.activityLog.health.job', { defaultValue: 'Job' }),
                t('admin.activityLog.health.cadence', { defaultValue: 'Declared cadence' }),
                t('admin.activityLog.health.lastRun', { defaultValue: 'Last run' }),
                t('admin.activityLog.health.lastSuccess', { defaultValue: 'Last success' }),
                t('admin.activityLog.health.state', { defaultValue: 'State' })
              ].map((h) => (
                <th
                  key={h}
                  className="text-left text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide px-3 py-2"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(health.jobs || []).map((job) => (
              <JobRow key={job.job} job={job} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HealthPanel;
